import { supabase } from './supabaseClient';

const COLUMN_ALIASES = {
  name: ['name', 'customer_name', 'customer name'],
  contact_number: ['contact_number', 'contact number', 'phone', 'mobile'],
  car_number: ['car_number', 'carnumber', 'car number', 'vehicle_registration', 'vehicle number'],
  car_model: ['car_model', 'car model', 'vehicle_model'],
  paid_amount: ['paid_amount', 'paidamount', 'paid amount', 'amount_paid', 'cod_amount'],
  delivered_datetime: ['delivered_datetime', 'delivered datetime', 'delivered_date', 'delivery_date'],
  expiry_datetime: ['expiry_datetime', 'expiry datetime', 'expires_at', 'expiry_date', 'expiry date', 'membership_expiry', 'membership expiry', 'valid till', 'validity', 'membership_validity', 'membership validity'],
  card_number: ['card_number', 'cardnumber', 'card number', 'membership_card_number'],
  address: ['address'],
  pincode: ['pincode', 'pin_code', 'pin code'],
  state: ['state'],
  email: ['email', 'email_id'],
  fuel_type: ['fuel_type', 'fuel type', 'fuel'],
  services_used: ['services_used', 'services used', 'used_services'],
  services_remaining: ['services_remaining', 'services remaining', 'remaining_services'],
  services_limit: ['services_limit', 'services limit', 'total_services'],
};

export const LEGACY_CUSTOMER_FIELDS = [
  {
    key: 'name',
    label: 'Customer Name',
    required: true,
    example: 'Rajesh Kumar',
    hint: 'Full name as on membership record',
  },
  {
    key: 'contact_number',
    label: 'Contact Number',
    required: true,
    example: '9876543210',
    hint: '10-digit mobile — used for app login OTP',
  },
  {
    key: 'car_number',
    label: 'Car Number',
    required: true,
    example: 'DL01AB1234',
    hint: 'Vehicle registration number',
  },
  {
    key: 'car_model',
    label: 'Car Model',
    required: true,
    example: 'Maruti Swift',
    hint: 'Brand + model; brand is auto-detected from first word',
  },
  {
    key: 'paid_amount',
    label: 'Paid Amount',
    required: true,
    example: '4999',
    hint: 'Amount customer paid — stored on subscription record',
  },
  {
    key: 'delivered_datetime',
    label: 'Delivered Datetime',
    required: true,
    example: '07 Mar 2023, 20:35',
    hint: 'Membership start date; format DD Mon YYYY, HH24:MI',
  },
  {
    key: 'expiry_datetime',
    label: 'Expiry Datetime',
    required: false,
    example: '07 Mar 2038, 20:35',
    hint: 'Membership end date; same format as delivered datetime. Auto-calculated if blank',
  },
  {
    key: 'card_number',
    label: 'Card Number',
    required: false,
    example: '7860687226265790',
    hint: '16-digit physical card; leave blank if missing — verify later',
  },
  {
    key: 'address',
    label: 'Address',
    required: false,
    example: 'Rohini Sector 7, Delhi',
    hint: 'Customer delivery address',
  },
  {
    key: 'pincode',
    label: 'Pincode',
    required: false,
    example: '110085',
    hint: '6-digit pincode',
  },
  {
    key: 'state',
    label: 'State',
    required: false,
    example: 'Delhi',
    hint: 'Can be blank if unknown',
  },
  {
    key: 'email',
    label: 'Email',
    required: false,
    example: 'rajesh@email.com',
    hint: 'Auto-generated as rsa+phone@roadserve.in if blank',
  },
  {
    key: 'fuel_type',
    label: 'Fuel Type',
    required: false,
    example: 'petrol',
    hint: 'petrol, diesel, cng, electric',
  },
  {
    key: 'services_used',
    label: 'Services Used',
    required: false,
    example: '1',
    hint: 'Kitni RSA services use ho chuki hain — blank = 0',
  },
  {
    key: 'services_remaining',
    label: 'Services Remaining',
    required: false,
    example: '1',
    hint: 'Kitni services bachi hain — blank = plan default (2 minus used)',
  },
];

const REQUIRED_KEYS = LEGACY_CUSTOMER_FIELDS.filter((f) => f.required).map((f) => f.key);

function sanitizeCell(val) {
  const v = String(val ?? '').trim();
  return v.replace(/^"+|"+$/g, '').trim();
}

const EMPTY_DATETIME_PLACEHOLDERS = new Set(['-', '—', 'na', 'n/a', '#n/a', 'nil', 'null', 'none', 'lifetime', '0', '.']);

export function normalizeLegacyDatetime(val) {
  let v = sanitizeCell(val).replace(/[\u00a0\u200b\ufeff]/g, ' ').replace(/\s+/g, ' ').trim();
  if (!v || EMPTY_DATETIME_PLACEHOLDERS.has(v.toLowerCase())) return '';

  const monthMap = {
    january: 'Jan', february: 'Feb', march: 'Mar', april: 'Apr', may: 'May', june: 'Jun',
    july: 'Jul', august: 'Aug', september: 'Sep', october: 'Oct', november: 'Nov', december: 'Dec',
    sept: 'Sep',
  };
  Object.entries(monthMap).forEach(([full, abbr]) => {
    v = v.replace(new RegExp(`\\b${full}\\b`, 'gi'), abbr);
  });
  v = v.replace(/\b(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)\b/gi, (m) => (
    m.charAt(0).toUpperCase() + m.slice(1).toLowerCase()
  ));
  v = v.replace(/(\d{1,2})[-/]([A-Za-z]{3})[-/](\d{2,4})/g, '$1 $2 $3');
  return v.trim();
}

function parseCsvLine(line) {
  const out = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        cur += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === ',' && !inQuotes) {
      out.push(cur);
      cur = '';
    } else {
      cur += ch;
    }
  }
  out.push(cur);
  return out;
}

function normalizeHeader(header) {
  return sanitizeCell(header).toLowerCase().replace(/\*$/, '').trim();
}

function resolveCanonicalKey(header) {
  const normalized = normalizeHeader(header);
  const found = Object.entries(COLUMN_ALIASES).find(([, aliases]) =>
    aliases.some((alias) => alias === normalized),
  );
  return found ? found[0] : null;
}

export function parseLegacyCustomerCsv(csvText) {
  const lines = String(csvText || '').replace(/^\uFEFF/, '').split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) {
    throw new Error('CSV must include a header row and at least one data row');
  }

  const headers = parseCsvLine(lines[0]).map(normalizeHeader);
  const canonicalIndexes = headers.map((h) => resolveCanonicalKey(h));

  const missing = REQUIRED_KEYS.filter(
    (key) => !canonicalIndexes.includes(key),
  );
  if (missing.length) {
    throw new Error(`Missing required columns: ${missing.join(', ')}`);
  }

  const rows = [];
  for (let i = 1; i < lines.length; i += 1) {
    const raw = lines[i];
    if (!raw.trim()) continue;
    const values = parseCsvLine(raw).map(sanitizeCell);
    const row = {};
    canonicalIndexes.forEach((key, idx) => {
      if (!key) return;
      row[key] = values[idx] || '';
    });
    rows.push(row);
  }

  return rows;
}

export function validateLegacyCustomerRow(row) {
  if (!row.name) return 'Customer name is required';
  const digits = String(row.contact_number || '').replace(/\D/g, '');
  const phone = digits.length === 12 && digits.startsWith('91') ? digits.slice(-10) : digits;
  if (phone.length !== 10) return 'Contact number must be 10 digits';
  if (!row.car_number) return 'Car number is required';
  const carNorm = String(row.car_number).replace(/[^A-Za-z0-9]/gi, '').toUpperCase();
  if (carNorm.length > 40) {
    return `Car number too long (${carNorm.length} chars, max 40): ${carNorm.slice(0, 50)}`;
  }
  if (!row.car_model) return 'Car model is required';
  if (String(row.car_model).length > 120) {
    return `Car model too long (${String(row.car_model).length} chars, max 120)`;
  }
  const amount = Number(String(row.paid_amount || '').replace(/[^\d.]/g, ''));
  if (!Number.isFinite(amount) || amount <= 0) return 'Paid amount is required';
  if (!row.delivered_datetime) return 'Delivered datetime is required';
  const deliveredNorm = normalizeLegacyDatetime(row.delivered_datetime);
  if (!deliveredNorm) return 'Delivered datetime format invalid (e.g. 07 Mar 2023, 20:35)';
  if (row.expiry_datetime) {
    const expiryNorm = normalizeLegacyDatetime(row.expiry_datetime);
    if (!expiryNorm) {
      // Treat -, NA, etc. as blank (auto-calculated on server)
    } else if (expiryNorm.replace(/[^\dA-Za-z,: ]/g, '').length < 8) {
      return `Expiry datetime format invalid: "${row.expiry_datetime}" (e.g. 07 Mar 2038, 20:35)`;
    }
  }
  if (row.card_number) {
    const card = String(row.card_number).replace(/\D/g, '');
    if (card.length !== 16 || !card.startsWith('7860')) {
      return 'Card number must be 16 digits starting with 7860, or leave blank';
    }
  }
  const usedRaw = String(row.services_used ?? '').trim();
  if (usedRaw !== '') {
    const used = Number(usedRaw);
    if (!Number.isInteger(used) || used < 0) return 'Services used must be a whole number ≥ 0';
  }
  const limitRaw = String(row.services_limit ?? '').trim();
  if (limitRaw !== '') {
    const limit = Number(limitRaw);
    if (!Number.isInteger(limit) || limit <= 0) return 'Services limit must be a whole number > 0';
    const used = usedRaw === '' ? 0 : Number(usedRaw);
    if (used > limit) return 'Services used cannot exceed services limit';
  }
  const remainingRaw = String(row.services_remaining ?? '').trim();
  if (remainingRaw !== '') {
    const remaining = Number(remainingRaw);
    if (!Number.isInteger(remaining) || remaining < 0) {
      return 'Services remaining must be a whole number ≥ 0';
    }
  }
  return null;
}

export function buildLegacyCustomerPayload(form) {
  const payload = {};
  LEGACY_CUSTOMER_FIELDS.forEach(({ key }) => {
    const val = form[key];
    if (val !== undefined && val !== null && String(val).trim() !== '') {
      if (key === 'delivered_datetime' || key === 'expiry_datetime') {
        const normalized = normalizeLegacyDatetime(val);
        if (normalized) payload[key] = normalized;
        return;
      }
      payload[key] = String(val).trim();
    }
  });
  return payload;
}

export function downloadLegacyCustomerSampleCsv() {
  const headers = LEGACY_CUSTOMER_FIELDS.map((f) => `${f.key}${f.required ? '*' : ''}`);
  const example = LEGACY_CUSTOMER_FIELDS.map((f) => f.example);

  const csv = [
    headers.join(','),
    example.map((v) => `"${v}"`).join(','),
    '"Rajesh Kumar","9876543210","DL01AB1234","Maruti Swift","4999","07 Mar 2023, 20:35","07 Mar 2038, 20:35","7860687226265790","Rohini Sector 7, Delhi","110085","Delhi","","petrol","1","1"',
    '"Priya Sharma","9123456789","MH12CD5678","Hyundai i20","499","15 Jan 2025, 14:20","15 Jan 2026, 14:20","","Pune Camp","411001","Maharashtra","","diesel","0","2"',
  ].join('\n');

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'legacy_customers_sample.csv';
  a.click();
  window.URL.revokeObjectURL(url);
}

export function findLegacyCustomerDuplicateRows(rows) {
  const errors = [];
  const seenPhones = new Map();
  const seenCars = new Map();
  const seenCards = new Map();

  rows.forEach((row, index) => {
    const rowNum = index + 1;
    const phone = String(row.contact_number || '').replace(/\D/g, '').slice(-10);
    const car = String(row.car_number || '').replace(/[^A-Za-z0-9]/gi, '').toUpperCase();
    const card = String(row.card_number || '').replace(/\D/g, '');

    if (phone.length === 10) {
      if (seenPhones.has(phone)) {
        errors.push({
          row: rowNum,
          name: row.name || '',
          contact_number: row.contact_number || '',
          error: `Duplicate contact number in CSV (also on row ${seenPhones.get(phone)})`,
        });
      } else {
        seenPhones.set(phone, rowNum);
      }
    }

    if (car) {
      if (seenCars.has(car)) {
        errors.push({
          row: rowNum,
          name: row.name || '',
          contact_number: row.contact_number || '',
          error: `Duplicate car number in CSV (also on row ${seenCars.get(car)})`,
        });
      } else {
        seenCars.set(car, rowNum);
      }
    }

    if (card) {
      if (seenCards.has(card)) {
        errors.push({
          row: rowNum,
          name: row.name || '',
          contact_number: row.contact_number || '',
          error: `Duplicate card number in CSV (also on row ${seenCards.get(card)})`,
        });
      } else {
        seenCards.set(card, rowNum);
      }
    }
  });

  return errors;
}

export function validateAllLegacyCustomerRows(rows) {
  const errors = [];
  rows.forEach((row, index) => {
    const validationError = validateLegacyCustomerRow(row);
    if (validationError) {
      errors.push({
        row: index + 1,
        name: row.name || '',
        contact_number: row.contact_number || '',
        error: validationError,
      });
    }
  });
  return [...errors, ...findLegacyCustomerDuplicateRows(rows)];
}

export class LegacyCustomerImportService {
  static async importOne(row) {
    const validationError = validateLegacyCustomerRow(row);
    if (validationError) {
      return { success: false, error: validationError };
    }

    const { data, error } = await supabase.rpc('admin_import_legacy_customer', {
      p_row: buildLegacyCustomerPayload(row),
    });

    if (error) {
      return { success: false, error: error.message || 'Import failed' };
    }

    if (!data?.success) {
      return { success: false, error: data?.error || 'Import failed' };
    }

    return { success: true, data };
  }

  static async importBatch(rows, onProgress, batchSize = 12) {
    const preflightErrors = validateAllLegacyCustomerRows(rows);
    if (preflightErrors.length > 0) {
      return {
        success: false,
        total: rows.length,
        imported: 0,
        failed: preflightErrors.length,
        errors: preflightErrors,
        message: 'No rows imported. Fix all errors and upload again.',
      };
    }

    const payloads = rows.map((row) => buildLegacyCustomerPayload(row));
    let imported = 0;

    if (onProgress) {
      onProgress({ total: rows.length, processed: 0, imported: 0, failed: 0 });
    }

    for (let start = 0; start < payloads.length; start += batchSize) {
      const chunk = payloads.slice(start, start + batchSize);
      const chunkStartRow = start + 1;

      const { data, error } = await supabase.rpc('admin_import_legacy_customers_batch', {
        p_rows: chunk,
      });

      if (error) {
        const isTimeout =
          error.code === '57014' ||
          String(error.message || '').toLowerCase().includes('timeout');
        return {
          success: false,
          total: rows.length,
          imported,
          failed: rows.length - imported,
          errors: [{
            row: chunkStartRow,
            name: rows[start]?.name || '',
            contact_number: rows[start]?.contact_number || '',
            error: isTimeout
              ? 'Database timeout — run supabase/fix-legacy-import-timeout.sql, then re-upload (already-imported rows will be skipped/updated).'
              : (error.message || 'Import failed — no rows were saved in this batch'),
          }],
          message: imported > 0
            ? `Import stopped around row ${chunkStartRow}. ${imported} customer(s) imported before failure.`
            : 'No rows imported. Fix all errors and upload again.',
        };
      }

      if (!data?.success) {
        const serverErrors = Array.isArray(data?.errors)
          ? data.errors.map((err) => ({
              row: Number(err.row || 0) + start,
              name: err.name || '',
              contact_number: err.contact_number || '',
              error: err.error || data?.message || 'Validation failed',
            }))
          : [{
              row: chunkStartRow,
              name: rows[start]?.name || '',
              contact_number: rows[start]?.contact_number || '',
              error: data?.message || data?.error || 'Import failed — no rows were saved in this batch',
            }];

        return {
          success: false,
          total: rows.length,
          imported,
          failed: rows.length - imported,
          errors: serverErrors,
          message: imported > 0
            ? `Import stopped around row ${chunkStartRow}. ${imported} customer(s) imported before failure.`
            : data?.message || 'No rows imported. Fix all errors and upload again.',
        };
      }

      imported += Number(data.imported) || chunk.length;

      if (onProgress) {
        onProgress({
          total: rows.length,
          processed: Math.min(start + chunk.length, rows.length),
          imported,
          failed: 0,
        });
      }
    }

    return {
      success: true,
      total: rows.length,
      imported,
      failed: 0,
      errors: [],
      message: `${imported} customer(s) imported successfully`,
    };
  }
}
